import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, interval, throwError } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';

import { ConfigService } from '../../shared/services/config.service';
import { LocationService } from '../../utilities/services/location.service';
import {
  GetSaleResponse,
  SaleState,
  SelectFunctionRequest,
  SelectFunctionResponse,
  SelectSeatRequest,
  SelectSeatResponse,
} from '../interfaces/sale.interface';

@Injectable({
  providedIn: 'root',
})
export class SaleService {
  private URL_API() {
    return (
      this.locationService.getApiUrl() || this.configService.getApiBaseUrl()
    );
  }

  // Estado de la venta actual
  private saleState = signal<SaleState>({
    currentSale: null,
    loading: false,
    error: null,
    expirationTime: null,
    timeRemaining: 0,
  });

  // Observable para componentes que necesiten suscribirse
  private saleSubject = new BehaviorSubject<SaleState>(this.saleState());

  // Timer para el countdown
  private countdownTimer$ = interval(1000);
  private stopTimer$ = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private locationService: LocationService,
    private configService: ConfigService,
    private router: Router,
    private toastr: ToastrService
  ) {
    this.initializeTimerSubscription();
    // Inicializar el servicio verificando si hay una venta activa
    this.initializeService();
  }

  /**
   * Inicializa el servicio verificando si hay una venta activa
   */
  public initializeService(): void {
    // Siempre verificar en el servidor al inicializar
    this.checkForActiveSale().subscribe();
  }

  /**
   * Verifica si hay una venta activa (método público para componentes)
   */
  public refreshSaleState(): Observable<GetSaleResponse> {
    return this.checkForActiveSale();
  }

  /**
   * Verifica si hay una venta activa sin mostrar loading
   */
  private checkForActiveSale(): Observable<GetSaleResponse> {
    return this.http
      .get<GetSaleResponse>(`${this.URL_API()}/process/getSale.php`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        tap((response) => {
          this.updateSaleState({
            loading: false,
            currentSale: {
              sale: response.sale,
              client: response.client,
              movie: response.movie,
              function: response.function,
              items: response.items,
              seats: response.seats,
              payments: response.payments,
              totalSeatsSale: response.totalSeatsSale,
              timeStartToExpirePoints: response.timeStartToExpirePoints,
              timeEndToExpirePoints: response.timeEndToExpirePoints,
            },
          });
          this.startExpirationTimer();
        }),
        catchError((error) => {
          // Si no hay venta activa, limpiar el estado local
          if (error.status === 404 || error.status === 400) {
            this.updateSaleState({
              loading: false,
              currentSale: null,
              error: null,
            });
          }
          return throwError(() => error);
        })
      );
  }

  // Getters públicos
  get currentSale() {
    return this.saleState().currentSale;
  }

  get loading() {
    return this.saleState().loading;
  }

  get timeRemaining() {
    return this.saleState().timeRemaining;
  }

  get saleState$() {
    return this.saleSubject.asObservable();
  }

  get saleStateSignal() {
    return this.saleState.asReadonly();
  }

  private updateSaleState(newState: Partial<SaleState>): void {
    const currentState = this.saleState();
    const updatedState = { ...currentState, ...newState };
    this.saleState.set(updatedState);
    this.saleSubject.next(updatedState);
  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken');
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
  }

  /**
   * Verifica si hay una venta activa en el servidor
   */
  getSale(): Observable<GetSaleResponse> {
    // Solo poner loading si no estamos ya en una operación
    if (!this.loading) {
      this.updateSaleState({ loading: true, error: null });
    }

    return this.http
      .get<GetSaleResponse>(`${this.URL_API()}/process/getSale.php`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        tap((response) => {
          this.updateSaleState({
            loading: false,
            currentSale: {
              sale: response.sale,
              client: response.client,
              movie: response.movie,
              function: response.function,
              items: response.items,
              seats: response.seats,
              payments: response.payments,
              totalSeatsSale: response.totalSeatsSale,
              timeStartToExpirePoints: response.timeStartToExpirePoints,
              timeEndToExpirePoints: response.timeEndToExpirePoints,
            },
          });
          this.startExpirationTimer();
        }),
        catchError((error) => {
          // Si no hay venta activa, no es realmente un error
          if (error.status === 404 || error.status === 400) {
            this.updateSaleState({
              loading: false,
              currentSale: null,
              error: null,
            });
          } else {
            this.updateSaleState({
              loading: false,
              error: 'Error obteniendo la venta activa',
            });
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Selecciona una función e inicia una nueva venta
   */
  selectFunction(functionId: string): Observable<SelectFunctionResponse> {
    this.updateSaleState({ loading: true, error: null });

    const request: SelectFunctionRequest = { id: functionId };

    return this.http
      .post<SelectFunctionResponse>(
        `${this.URL_API()}/process/selectFunction.php?id=${functionId}`,
        request,
        { headers: this.getAuthHeaders() }
      )
      .pipe(
        tap((response) => {
          if (response.status === 200) {
            this.updateSaleState({
              loading: false,
              currentSale: response.data,
            });
            this.startExpirationTimer();
            this.toastr.success('Nueva venta iniciada', 'Éxito');
          }
        }),
        catchError((error) => {
          this.updateSaleState({
            loading: false,
            error: error.error?.message || 'Error iniciando la venta',
          });
          this.toastr.error('No se pudo iniciar la venta', 'Error');
          return throwError(() => error);
        })
      );
  }

  /**
   * Selecciona o deselecciona un asiento
   */
  selectSeat(
    seatId: string,
    select: boolean = true
  ): Observable<SelectSeatResponse> {
    this.updateSaleState({ loading: true, error: null });

    const request: SelectSeatRequest = {
      seatId,
      status: select ? 'available' : 'selected', // available = seleccionar, selected = deseleccionar
    };

    return this.http
      .post<SelectSeatResponse>(
        `${this.URL_API()}/process/selectSeat.php`,
        request,
        { headers: this.getAuthHeaders() }
      )
      .pipe(
        tap((response) => {
          if (response.sale && this.currentSale) {
            // Después de seleccionar el asiento exitosamente,
            // refrescar la venta completa para obtener totales actualizados
            this.refreshSaleState().subscribe({
              next: (refreshedSale) => {
                // Mostrar notificación de éxito
                if (select) {
                  this.toastr.success(`Asiento seleccionado`, 'Éxito');
                } else {
                  this.toastr.warning(`Asiento deseleccionado`, 'Éxito');
                }
              },
              error: (error) => {
                console.error(
                  'Error refreshing sale after seat selection:',
                  error
                );
                // Fallback: actualizar solo con los datos parciales disponibles
                if (this.currentSale) {
                  const updatedSale = {
                    ...this.currentSale,
                    seats: response.seats,
                    totalSeatsSale: response.totalSeatsSale,
                  };

                  this.updateSaleState({
                    loading: false,
                    currentSale: updatedSale,
                  });
                } else {
                  this.updateSaleState({ loading: false });
                }

                if (select) {
                  this.toastr.success(`Asiento seleccionado`, 'Éxito');
                } else {
                  this.toastr.warning(`Asiento deseleccionado`, 'Éxito');
                }
              },
            });
          } else {
            console.error('Unexpected response or no current sale:', response);
            this.updateSaleState({ loading: false });
          }
        }),
        catchError((error) => {
          console.error('SelectSeat error:', error);
          this.updateSaleState({
            loading: false,
            error: error.error?.message || 'Error seleccionando asiento',
          });
          this.toastr.error('No se pudo seleccionar el asiento', 'Error');
          return throwError(() => error);
        })
      );
  }

  /**
   * Verifica si hay una venta activa para una función específica
   * Si no hay venta o es de otra función, inicia una nueva
   */
  ensureSaleForFunction(
    functionId: string
  ): Observable<SelectFunctionResponse | GetSaleResponse> {
    // Primero verificar si hay una venta activa
    return new Observable((observer) => {
      // Solo poner loading si no hay venta activa aún
      if (!this.currentSale) {
        this.updateSaleState({ loading: true, error: null });
      }

      this.getSale().subscribe({
        next: (currentSale) => {
          // Si hay una venta activa y es de la misma función, la mantenemos
          if (currentSale.function?.id === functionId) {
            // Asegurar que loading esté en false
            this.updateSaleState({ loading: false });
            observer.next(currentSale);
            observer.complete();
          } else {
            // Si es de otra función, iniciamos una nueva venta
            this.selectFunction(functionId).subscribe({
              next: (newSale) => {
                observer.next(newSale);
                observer.complete();
              },
              error: (error) => observer.error(error),
            });
          }
        },
        error: (error) => {
          console.log('No active sale found, creating new one');
          // No hay venta activa, crear una nueva
          this.selectFunction(functionId).subscribe({
            next: (newSale) => {
              observer.next(newSale);
              observer.complete();
            },
            error: (error) => observer.error(error),
          });
        },
      });
    });
  }

  /**
   * Inicia el timer de expiración de la venta
   */
  private startExpirationTimer(): void {
    if (!this.currentSale) return;

    // Calcular tiempo de expiración (por defecto 10 minutos desde ahora)
    const expirationTime = new Date();
    expirationTime.setMinutes(expirationTime.getMinutes() + 10);

    this.updateSaleState({ expirationTime });
    this.stopTimer$.next(false);

    // Iniciar countdown
    this.countdownTimer$.pipe(takeUntil(this.stopTimer$)).subscribe(() => {
      const now = new Date().getTime();
      const expiration = this.saleState().expirationTime?.getTime() || 0;
      const timeRemaining = Math.max(0, Math.floor((expiration - now) / 1000));

      this.updateSaleState({ timeRemaining });

      // Si el tiempo se agotó, limpiar la venta
      if (timeRemaining <= 0) {
        this.handleSaleExpiration();
      }
    });
  }

  /**
   * Maneja la expiración de la venta
   */
  private handleSaleExpiration(): void {
    this.stopTimer$.next(true);
    this.updateSaleState({
      currentSale: null,
      expirationTime: null,
      timeRemaining: 0,
    });

    this.toastr.warning(
      'Tu sesión de compra ha expirado. Los asientos seleccionados han sido liberados.',
      'Sesión Expirada',
      { timeOut: 8000 }
    );

    // Redirigir a la página principal si está en el checkout
    if (this.router.url.includes('/checkout')) {
      this.router.navigate(['/']);
    }
  }

  /**
   * Limpia la venta actual (cancelar compra)
   */
  clearSale(): void {
    this.stopTimer$.next(true);
    this.updateSaleState({
      currentSale: null,
      loading: false,
      error: null,
      expirationTime: null,
      timeRemaining: 0,
    });
  }

  /**
   * Obtiene información resumida de la venta actual
   */
  getSaleSummary(): {
    totalSeats: number;
    totalProducts: number;
    totalAmount: number;
    movieName: string;
    functionDate: string;
    functionTime: string;
  } | null {
    if (!this.currentSale) return null;

    const totalSeats = parseInt(this.currentSale.totalSeatsSale || '0');
    const totalProducts =
      this.currentSale.items?.reduce((acc, item) => {
        if (item.productId) {
          return acc + parseInt(item.quantity);
        }
        return acc;
      }, 0) || 0;

    return {
      totalSeats,
      totalProducts,
      totalAmount: this.currentSale.sale.total || 0,
      movieName: this.currentSale.movie.name,
      functionDate: this.currentSale.function.date,
      functionTime: this.currentSale.function.schedule,
    };
  }

  /**
   * Actualiza el estado de la venta desde una respuesta de API
   * Para casos simples, refresca el estado completo desde el servidor
   */
  updateSaleFromResponse(response: any): void {
    try {
      if (response?.client) {
        // Para mantener consistencia de datos, refrescar el estado completo
        this.refreshSaleState().subscribe({
          error: (error) => {
            console.error('SaleService: Error refrescando estado:', error);
          },
        });
      } else {
        console.warn('SaleService: Respuesta API sin éxito', response);
      }
    } catch (error) {
      console.error(
        'SaleService: Error actualizando estado desde respuesta',
        error
      );
    }
  }

  /**
   * Formatea el tiempo restante en formato MM:SS
   */
  formatTimeRemaining(): string {
    const timeRemaining = this.saleState().timeRemaining;
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  }

  private initializeTimerSubscription(): void {
    // Inicializar el subject para controlar el timer
    this.stopTimer$.next(true);
  }

  clearError(): void {
    this.updateSaleState({ error: null });
  }
}
